import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  getVisit,
  PlannedVisit,
  deletePlannedReport,
  updatePlannedReport,
  UpdatePlannedReportDto,
  fetchReport,
  joinPlannedVisit,
  linkPlannedVisitReport,
} from '../../api/reportsApi';
import { Report, ReportState } from '../../types/Rows';
import { addReport, fetchTemplatesByStudy } from '../../api/studyApi';
import ScientiaHeader from '../../components/ScientiaHeader';
import ReportDropdown from '../../components/ReportDropdown';
import DeleteIcon from '../../components/icons/DeleteIcon';
import { parseParticipants } from '../../utils/plannedVisitUtils';
import LinkReportPanel from './LinkReportPanel';
import { LoadingRegion, Skeleton } from '../../components/ui/Skeleton';

export const VisitPage: React.FC = () => {
  const { visitId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const currentUserEmail = instance.getActiveAccount()?.username;
  const [editedDateStart, setEditedDateStart] = useState('');

  const { data: visit, isLoading, error } = useQuery<PlannedVisit>(
    ['visit', visitId],
    () => getVisit(visitId!),
    {
      enabled: !!visitId,
    }
  );

  const {
    data: report,
    isLoading: isReportLoading,
    error: reportError,
  } = useQuery<Report>(
    ['report', visit?.reportId],
    () => fetchReport(visit!.reportId!),
    {
      enabled: !!visit?.reportId,
    }
  );

  const getReportStateLabel = (state?: ReportState) => {
    switch (state) {
      case ReportState.INPROGRESS:
        return t('inProgress');
      case ReportState.TO_VERIFY:
        return t('toVerify');
      case ReportState.TO_CORRECT:
        return t('toCorrect');
      case ReportState.ACCEPTED:
        return t('accepted');
      default:
        return t('unknown');
    }
  };

  // Fetch templates for the study
  const { data: templates } = useQuery(
    ['templates', visit?.studyId],
    () => fetchTemplatesByStudy(visit!.studyId!),
    {
      enabled: !!visit?.studyId,
    }
  );

  // Create report and assign it to planned visit
  const { mutate: addReportMutation } = useMutation(
    async ({
      resortId,
      templateId,
    }: {
      resortId: string;
      templateId: string;
    }) => {
      const reportResponse = await addReport(visit!.studyId!, resortId, templateId);
      const reportId =
        // axios response might wrap the payload under data
        (reportResponse as any)?.data?.id ??
        (reportResponse as any)?.id ??
        (typeof reportResponse === 'string' ? reportResponse : undefined);

      if (!reportId) {
        throw new Error('Unable to determine created report id');
      }
      // osobny endpoint: PUT planned/{id} wysyłał uczestnikom zaproszenie ponownie
      await linkPlannedVisitReport(visit!.id, String(reportId));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['visit', visitId]);
      },
    }
  );

  const refreshVisit = () => {
    queryClient.invalidateQueries(['visit', visitId]);
    queryClient.invalidateQueries(['visitReportCandidates', visitId]);
  };

  const { mutate: unlinkReportMutation, isLoading: isUnlinking } = useMutation(
    async () => linkPlannedVisitReport(visitId!, null),
    { onSuccess: refreshVisit }
  );

  const handleUnlinkReport = () => {
    if (window.confirm(t('confirmUnlinkReport'))) {
      unlinkReportMutation();
    }
  };

  // Delete visit mutation
  const { mutate: deleteVisitMutation } = useMutation(
    async () => {
      await deletePlannedReport(visitId!);
    },
    {
      onSuccess: () => {
        navigate('/calendar');
      },
    }
  );

  const { mutate: joinVisitMutation, isLoading: isJoining } = useMutation(
    async () => joinPlannedVisit(visitId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['visit', visitId]);
      },
    }
  );

  // Zgłoszenie z 20.10: po usunięciu wizyty z raportem przypomnienia dalej przychodzą, bo liczą się
  // z raportu. Ostrzeżenie zamiast cichego usunięcia (raport zostaje, nic nie jest kasowane).
  const handleDeleteVisit = () => {
    const message = visit?.reportId ? t('confirmDeletePlannedVisitWithReport') : t('confirmDeletePlannedReport');
    if (window.confirm(message)) {
      deleteVisitMutation();
    }
  };

  const { mutate: updateStartDateMutation, isLoading: isUpdatingStartDate } = useMutation(
    async (newDateStart: string) => {
      if (!visit) return;

      const payload: UpdatePlannedReportDto = {
        id: visit.id,
        templateId: visit.templateId || '',
        studyId: visit.studyId || '',
        resortId: visit.resortId || '',
        dateStart: newDateStart,
        dateEnd: newDateStart,
        name: visit.name,
        participants: visit.participants,
        isRemote: !!visit.isRemote,
        reportId: visit.reportId,
      };

      await updatePlannedReport(visit.id, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['visit', visitId]);
      },
    }
  );

  useEffect(() => {
    if (visit?.dateStart) {
      const dateOnly = visit.dateStart.split('T')[0] || visit.dateStart;
      setEditedDateStart(dateOnly);
    }
  }, [visit?.dateStart]);

  // Układ strony wizyty z prawdziwymi etykietami pól, dane jako skeleton
  if (isLoading) {
    const label = "text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2";
    return (
      <LoadingRegion label={t('loading')} className="min-h-screen bg-[var(--sc-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <button onClick={() => navigate('/calendar')} className="text-[#F4A832] hover:text-[#e09620] font-medium cursor-pointer">
              ← {t('backToCalendar')}
            </button>
          </div>
          <div className="bg-[var(--sc-surface)] shadow rounded-lg overflow-hidden">
            <div className="px-6 py-5 border-b border-[var(--sc-border)] flex justify-between items-center">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-9 w-28 rounded-full" />
            </div>
            <div className="px-6 py-5 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><h3 className={label}>{t('study')}</h3><Skeleton className="h-4 w-48" /></div>
                <div><h3 className={label}>{t('resort')}</h3><Skeleton className="h-4 w-56" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><h3 className={label}>{t('visitDate')}</h3><Skeleton className="h-9 w-full max-w-xs rounded-lg" /></div>
                <div><h3 className={label}>{t('Remote')}</h3><Skeleton className="h-4 w-12" /></div>
              </div>
              <div>
                <h3 className={label}>{t('croParticipants')}</h3>
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-32 rounded-full" />
                  <Skeleton className="h-7 w-28 rounded-full" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </LoadingRegion>
    );
  }

  if (error || !visit) {
    return (
      <div className="min-h-screen bg-[var(--sc-bg)]">
        <ScientiaHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-red-600">{t('errorLoadingVisitData')}</div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[var(--sc-bg)]">
      {/* <ScientiaHeader /> */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/calendar')}
            className="text-[#F4A832] hover:text-[#e09620] font-medium cursor-pointer"
          >
            ← {t('backToCalendar')}
          </button>
        </div>

        <div className="bg-[var(--sc-surface)] shadow rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-[var(--sc-border)] flex justify-between items-center">
            <h1 className="text-[15px] font-semibold text-[var(--sc-text)]">
              {visit.name || t('visitDetails')}
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => joinVisitMutation()}
                disabled={isJoining}
                className="px-4 py-2 text-sm font-medium text-[#0F0F0F] bg-[#F4A832] hover:bg-[#e09620] rounded-full cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isJoining ? t('loading') : t('join')}
              </button>
              {currentUserEmail === visit.createdBy && (
                <button
                  onClick={handleDeleteVisit}
                  className="p-2 cursor-pointer rounded-lg hover:bg-[var(--sc-bg)] text-red-600 hover:text-red-800"
                  title={t('delete')}
                >
                  <DeleteIcon />
                </button>
              )}
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            {/* Study and Resort Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('study')}</h3>
                <div className="flex items-center gap-3 flex-wrap ">
                  <p className="text-sm text-[var(--sc-text)]">{visit.studyName || 'N/A'}</p>
                  {visit.studyId && (
                    <button

                      onClick={() => navigate(`/study/${visit.studyId}`)}
                      className="text-[#F4A832] hover:text-[#e09620] font-medium cursor-pointer"
                    >
                      {t('goToStudyPage')}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('resort')}</h3>
                <p className="text-sm text-[var(--sc-text)]">{visit.resortName || 'N/A'}</p>
              </div>
            </div>

            {/* Date Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('visitDate')}</h3>
                <input
                  type="date"
                  value={editedDateStart}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setEditedDateStart(newDate);
                    if (newDate) {
                      updateStartDateMutation(newDate);
                    }
                  }}
                  className="block w-full max-w-xs rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm text-[var(--sc-text)] focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
                  disabled={isUpdatingStartDate}
                />
                {isUpdatingStartDate && (
                  <p className="text-sm text-[var(--sc-muted)]">{t('updating')}</p>
                )}
              </div>
            </div>

            {/* Remote Status */}
            {visit.isRemote !== undefined && (
              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('Remote')}</h3>
                <p className="text-sm text-[var(--sc-text)]">
                  {visit.isRemote ? t('yes') : t('no')}
                </p>
              </div>
            )}

            {/* Participants */}
            {visit.participants && (
              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('croParticipants')}</h3>
                <div className="flex flex-wrap gap-2">
                  {parseParticipants(visit.participants).map((participant) => (
                    <span
                      key={participant}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-[#FFF8ED] text-[#C47A0A] border border-[#F4A832]/30"
                    >
                      {participant}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Comment */}
            {visit.comment && (
              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('comment')}</h3>
                <p className="text-sm text-[var(--sc-text)] whitespace-pre-wrap">{visit.comment}</p>
              </div>
            )}

            {/* Report Link or Create Report */}
            {visit.reportId ? (
              <div>
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('associatedReport')}</h3>
                <div className="space-y-3">
                  {isReportLoading && <p className="text-sm text-[var(--sc-muted)]">{t('loading')}</p>}
                  {reportError && (
                    <p className="text-sm text-red-600">{t('errorLoadingReportData')}</p>
                  )}

                  {report && (
                    <div className="bg-[var(--sc-bg)] border border-[var(--sc-border)] rounded-lg p-4 text-sm text-[var(--sc-text)] space-y-1">
                      <p>
                        <span className="font-medium">{t('reportName')}:</span> {report.name}
                      </p>
                      <p>
                        <span className="font-medium">{t('reportStatus')}:</span>{' '}
                        {getReportStateLabel(report.state)}
                      </p>
                      <p>
                        <span className="font-medium">{t('study')}:</span> {report.study}
                      </p>
                      <p>
                        <span className="font-medium">{t('resort')}:</span> {report.resort}
                      </p>
                    </div>
                  )}

                  {visit.studyId && visit.resortId && templates && templates.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)]">{t('changeReportType')}</h4>
                      <ReportDropdown
                        resortId={visit.resortId}
                        templates={templates.filter((x) => x.studyId === visit.studyId)}
                        addReportMutation={addReportMutation}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => navigate(`/reports/${visit.reportId}`)}
                      className="text-[#F4A832] hover:text-[#e09620] font-medium cursor-pointer"
                    >
                      {t('viewReport')}
                    </button>
                    <button
                      onClick={handleUnlinkReport}
                      disabled={isUnlinking}
                      className="text-sm text-[var(--sc-muted)] hover:text-red-600 cursor-pointer disabled:opacity-60"
                    >
                      {t('unlinkReport')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              visit.studyId && visit.resortId && (
                <div className="space-y-4">
                  {templates && templates.length > 0 && (
                    <div>
                      <h3 className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--sc-muted)] mb-2">{t('createReport')}</h3>
                      <ReportDropdown
                        resortId={visit.resortId}
                        templates={templates.filter((x) => x.studyId === visit.studyId)}
                        addReportMutation={addReportMutation}
                      />
                    </div>
                  )}
                  <LinkReportPanel
                    visitId={visit.id}
                    getReportStateLabel={getReportStateLabel}
                    onLinked={refreshVisit}
                  />
                </div>
              )
            )}

            {/* Metadata */}
            <div className="pt-6 border-t border-[var(--sc-border)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-[var(--sc-muted)]">
                <div>
                  <span className="font-medium">{t('createdBy')}:</span> {visit.createdBy || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">{t('created')}:</span> {formatDate(visit.created)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
